/**
 * The two ways to price a tier, and the arithmetic that keeps them in step.
 *
 * e-conomic asks for cost and the SELLING price and derives the profit; our form
 * has always asked for cost and the MARKUP and derived the price. Both are here:
 * sale price is primary, markup is a secondary box, and each fills the other.
 *
 * `markup` is profit over COST — `price = cost x (1 + markup/100)`.
 * `margin` is the same profit over REVENUE, which is what e-conomic prints as
 * "Avance i %". They are different numbers on every sale and are never mixed.
 */

/** Which box the admin actually typed in. Decides what gets sent. */
export type TierEntryMode = 'sale' | 'markup';

const round2 = (n: number) => Math.round(n * 100) / 100;
/** Per-piece prices keep up to 4 decimals — the backend stores sub-cent unit prices. */
const round4 = (n: number) => Math.round(n * 10000) / 10000;

/**
 * Parses an amount the way this panel PRINTS one.
 *
 * Everything here is formatted `da-DK` — `1.200,50` — so a Danish keyboard types
 * `112,13`, and bare `parseFloat` stops at the comma and returns `112`. A cost
 * silently losing its decimals is the worst failure this file could have, so
 * the comma is handled at the single point every caller goes through.
 *
 * A comma means it is a Danish number: dots are thousands separators and go.
 * Without one, it is parsed as written, so `112.13` typed on an English
 * keyboard still means what it looks like.
 */
export const parseAmount = (s: string): number | null => {
	const trimmed = s.trim();
	if (!trimmed) return null;
	const normalised = trimmed.includes(',')
		? trimmed.replace(/\./g, '').replace(',', '.')
		: trimmed;
	const n = parseFloat(normalised);
	return Number.isFinite(n) ? n : null;
};

const num = parseAmount;

/** Sale price implied by a markup. Empty string when it cannot be computed. */
export function saleFromMarkup(cost: string, markup: string): string {
	const c = num(cost);
	const m = num(markup);
	if (c == null || m == null) return '';
	return String(round4(c * (1 + m / 100)));
}

/**
 * Markup implied by a sale price. Empty at zero cost: everything is infinite
 * markup over nothing, and a made-up number there is worse than a blank.
 */
export function markupFromSale(cost: string, sale: string): string {
	const c = num(cost);
	const s = num(sale);
	if (c == null || s == null || c === 0) return '';
	return String(round2(((s - c) / c) * 100));
}

export type TierEconomics = {
	/** Profit per unit — e-conomic's Avance (DKK) per unit. */
	profitUnit: number;
	/** Profit on the whole tier — e-conomic's Avance (DKK). */
	profitTotal: number;
	/** Profit over revenue — e-conomic's Avance i %. Null at a zero sale price. */
	marginPercentage: number | null;
	/** What the client pays for the tier — e-conomic's I alt (DKK). */
	lineTotal: number;
};

/**
 * The four figures e-conomic shows under a quote line, for input the admin has
 * not saved yet. Once saved the server owns them (`marginUnit`, `marginTotal`,
 * `marginPercentage`, `totalPrice`); this is the live preview of a form, which
 * is the one case the frontend does compute them.
 */
export function tierEconomics(cost: string, sale: string, quantity: number): TierEconomics | null {
	const c = num(cost);
	const s = num(sale);
	if (c == null || s == null) return null;
	const profitUnit = s - c;
	return {
		profitUnit,
		profitTotal: profitUnit * quantity,
		marginPercentage: s === 0 ? null : (profitUnit / s) * 100,
		lineTotal: s * quantity,
	};
}

/** The three linked price fields, under neutral names each form maps onto. */
export type PriceTriple = {
	cost: string;
	salePrice: string;
	markup: string;
	entryMode: TierEntryMode;
};

/**
 * Applies one edit and keeps the other two fields in step.
 *
 * Sale price and markup are two readings of one decision, so editing either
 * fills the other, and `entryMode` records which was typed — that is the one
 * that gets sent. Editing the COST holds whichever the admin chose and lets the
 * other follow: change a supplier's cost with a markup in mind and the price
 * moves; change it with a price in mind and the markup does.
 *
 * Shared by all three pricing forms — the offer modal, the batch wizard and the
 * catalogue ladder — because three copies of this branching is exactly how they
 * would come to disagree.
 */
export function applyPriceEdit(
	current: PriceTriple,
	field: 'cost' | 'salePrice' | 'markup',
	value: string
): PriceTriple {
	const next: PriceTriple = { ...current, [field]: value };
	if (field === 'salePrice') {
		next.entryMode = 'sale';
		next.markup = markupFromSale(next.cost, value);
	} else if (field === 'markup') {
		next.entryMode = 'markup';
		next.salePrice = saleFromMarkup(next.cost, value);
	} else if (current.entryMode === 'markup') {
		next.salePrice = saleFromMarkup(value, next.markup);
	} else {
		next.markup = markupFromSale(value, next.salePrice);
	}
	return next;
}

/**
 * A tier with a cost and neither a sale price nor a markup.
 *
 * The backend derives a price from a markup, a sale price or a fixed amount —
 * given none of them it stores a cost with no client price. That's a real
 * state (supplier prices land that way too), so the forms let it through and
 * only say so: sending the offer is where the backend refuses it.
 */
export function isUnpriced(v: { cost: string; salePrice: string; markup: string }): boolean {
	const c = num(v.cost);
	if (c == null || c <= 0) return false;
	return num(v.salePrice) == null && num(v.markup) == null;
}

/** A sale price under cost is rejected by the backend; catch it in the form. */
export function belowCost(cost: string, sale: string): boolean {
	const c = num(cost);
	const s = num(sale);
	if (c == null || s == null) return false;
	return s < c;
}

/**
 * The price half of a tier's mutation input.
 *
 * `unitSalePrice`, `markupPercentage` and `marginAmount` are mutually exclusive,
 * so exactly one goes out: whichever box the admin edited last. Sending both
 * would be rejected, and guessing which one they meant is worse than recording
 * it as they typed.
 */
export function pricingPayload(v: {
	cost: string;
	salePrice: string;
	markup: string;
	entryMode: TierEntryMode;
	currency: string;
}) {
	const base = { baseUnitPrice: num(v.cost) ?? 0, currency: v.currency };
	if (v.entryMode === 'sale') {
		const s = num(v.salePrice);
		return s == null ? base : { ...base, unitSalePrice: s };
	}
	const m = num(v.markup);
	return m == null ? base : { ...base, markupPercentage: m };
}
